import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Loader2, Sparkles, ArrowDown, Brain, Handshake, Clock, ListChecks, ChevronDown, ChevronRight, RefreshCw } from "lucide-react";

const fmt = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const sameDay = (a, b) => { try { return new Date(a).toDateString() === new Date(b).toDateString(); } catch { return false; } };

const BrainPanel = ({ brain, building, onBuild, onUseReply, canReply }) => {
  const [open, setOpen] = useState(true);
  if (!brain) return null;
  if (!brain.exists) {
    return (
      <div className="px-4 py-3 border-b border-cyan-400/15 bg-gradient-to-r from-cyan-500/[0.07] to-transparent">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Brain size={15} className="text-cyan-300 shrink-0" />
            <span className="text-[12px] text-zinc-300 truncate">Let the AI study this whole conversation and brief you.</span>
          </div>
          <button data-testid="brain-build" onClick={onBuild} disabled={building} className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-tactical uppercase tracking-wider rounded-lg px-3 h-8 bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-bold disabled:opacity-50">
            {building ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Brief me · 1 credit
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="border-b border-cyan-400/15 bg-gradient-to-b from-cyan-500/[0.06] to-transparent">
      <div className="flex items-center justify-between px-4 py-2">
        <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-1.5 text-[11px] font-tactical uppercase tracking-wider text-cyan-200">
          {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} <Brain size={13} /> Relationship brief
          {brain.stale && <span className="text-[9px] text-amber-300 normal-case font-mono">· new messages</span>}
        </button>
        <button data-testid="brain-refresh" onClick={onBuild} disabled={building} title="Refresh (1 credit)" className="inline-flex items-center gap-1 text-[10px] font-mono text-cyan-300 border border-cyan-400/30 rounded px-2 py-0.5 hover:bg-cyan-500/15 disabled:opacity-50">
          {building ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh
        </button>
      </div>
      {open && (
        <div className="px-4 pb-3 space-y-2 text-[12px]">
          {brain.who && <div className="text-zinc-200"><span className="text-cyan-300 font-semibold">{brain.who}</span></div>}
          {brain.recap && <div className="text-zinc-300"><span className="text-zinc-500">Last: </span>{brain.recap}</div>}
          {brain.why_it_matters && <div className="text-zinc-300"><span className="text-emerald-300 font-semibold">Why it matters: </span>{brain.why_it_matters}</div>}
          {brain.deal && <div className="flex items-start gap-1.5 text-emerald-200"><Handshake size={13} className="mt-0.5 shrink-0" /><span>{brain.deal}</span></div>}
          {brain.stay_in_touch && <div className="flex items-start gap-1.5 text-amber-200"><Clock size={13} className="mt-0.5 shrink-0" /><span>{brain.stay_in_touch}</span></div>}
          {brain.outstanding?.length > 0 && (
            <div className="flex items-start gap-1.5 text-zinc-300"><ListChecks size={13} className="mt-0.5 shrink-0 text-cyan-300" />
              <ul className="list-disc pl-4 space-y-0.5">{brain.outstanding.map((o, i) => <li key={i}>{o}</li>)}</ul>
            </div>
          )}
          {brain.facts?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-0.5">{brain.facts.map((f, i) => <span key={i} className="text-[10px] bg-white/[0.05] border border-white/10 rounded-full px-2 py-0.5 text-zinc-300">{f}</span>)}</div>
          )}
          {brain.suggested_reply && canReply && (
            <button data-testid="brain-use-reply" onClick={() => onUseReply(brain.suggested_reply)} className="mt-1 w-full text-left rounded-lg border border-cyan-400/30 bg-cyan-500/10 hover:bg-cyan-500/20 px-3 py-2 transition-colors">
              <div className="text-[9px] uppercase tracking-wider text-cyan-300/70 font-mono mb-0.5 flex items-center gap-1"><Sparkles size={10} /> Suggested reply — tap to use</div>
              <div className="text-[12px] text-cyan-50">{brain.suggested_reply}</div>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default function MessageThread({ pid, onClose, onReplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [brain, setBrain] = useState(null);
  const [building, setBuilding] = useState(false);
  const bottomRef = useRef(null);

  const load = (scroll = true) => api.messageThread(pid).then((d) => { setData(d); if (scroll) setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "auto" }), 50); }).catch(() => setData(null)).finally(() => setLoading(false));
  useEffect(() => { if (pid) { load(); api.imessageBrainGet(pid).then(setBrain).catch(() => setBrain(null)); } }, [pid]);

  const canReply = data?.channel === "sms";
  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { const r = await api.imessageReply(pid, text.trim()); toast.success(r.message || "Sent"); setText(""); await load(); onReplied?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };
  const buildBrain = async () => {
    setBuilding(true);
    try { const b = await api.imessageBrainBuild(pid); setBrain({ ...b, exists: true, stale: false }); toast.success("Brief ready · 1 credit used"); }
    catch (e) {
      if (e.response?.status === 402) toast.error("Not enough credits — top up to use the AI brain");
      else toast.error(e.response?.data?.detail || "Could not build brief");
    } finally { setBuilding(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-[#070b12] flex flex-col" data-testid="message-thread">
      <div className="w-full h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 h-14 border-b border-white/10 shrink-0 bg-[#0a1017]">
          <button data-testid="thread-back" onClick={onClose} className="flex items-center gap-2 text-zinc-300 hover:text-white min-w-0">
            <span className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 shrink-0"><X size={16} /></span>
            <span className="text-left min-w-0">
              <span className="block font-tactical font-bold text-zinc-100 text-sm truncate">{data?.name || "Conversation"}</span>
              <span className="block text-[10px] font-mono text-zinc-500 truncate">{data?.handle} · {data?.count || 0} messages</span>
            </span>
          </button>
        </div>

        {/* AI relationship brain */}
        <BrainPanel brain={brain} building={building} onBuild={buildBrain} onUseReply={(t) => setText(t)} canReply={canReply} />

        {/* Messages */}
        <div className="flex-1 overflow-y-auto thin-scroll w-full">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 space-y-1">
          {loading ? (
            <div className="h-full grid place-items-center text-zinc-500 text-sm font-mono"><Loader2 size={16} className="animate-spin" /></div>
          ) : (data?.messages || []).length === 0 ? (
            <div className="h-full grid place-items-center text-zinc-600 text-sm">No messages in this conversation.</div>
          ) : data.messages.map((m, i) => {
            const prev = data.messages[i - 1];
            const showDate = !prev || !sameDay(prev.date, m.date);
            return (
              <div key={i}>
                {showDate && <div className="text-center text-[10px] font-mono text-zinc-600 my-3">{fmt(m.date)}</div>}
                <div className={`flex ${m.is_from_me ? "justify-end" : "justify-start"}`}>
                  <div data-testid={`thread-bubble-${i}`} className={`max-w-[78%] px-3.5 py-2 rounded-2xl text-[14px] leading-snug whitespace-pre-wrap break-words ${m.is_from_me ? "bg-[#2563eb] text-white rounded-br-md" : "bg-[#262b33] text-zinc-100 rounded-bl-md"}`}>
                    {m.text || <span className="opacity-50 italic">attachment</span>}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
          </div>
        </div>

        {/* Composer */}
        {canReply ? (
          <div className="border-t border-white/10 p-3 shrink-0 bg-[#0a1017]">
            <div className="flex items-end gap-2 max-w-3xl mx-auto">
              <textarea data-testid="thread-reply-input" rows={1} value={text} maxLength={1500} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={`iMessage ${data?.name || ""}…`} className="flex-1 bg-[#0b0e14] border border-white/10 rounded-2xl px-4 py-2.5 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 resize-none max-h-28" />
              <button data-testid="thread-send" onClick={send} disabled={sending} className="w-10 h-10 grid place-items-center rounded-full bg-cyan-500 hover:bg-cyan-400 text-[#04070c] disabled:opacity-50 shrink-0">{sending ? <Loader2 size={15} className="animate-spin" /> : <ArrowDown size={16} className="-rotate-90" />}</button>
            </div>
            <p className="text-[10px] text-zinc-600 mt-1.5 text-center">Sends from your Mac bridge · Enter to send, Shift+Enter for a new line</p>
          </div>
        ) : (
          <div className="border-t border-white/10 p-3 text-center text-[11px] text-zinc-500 shrink-0">Replies for this channel aren't supported here yet.</div>
        )}
      </div>
    </div>
  );
}
